import { cn } from "@/lib/utils";
import { isRtlText } from "@/lib/utils/index";
import Image from "next/image";
import { memo, useMemo } from "react";
import Latex from "react-latex-next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkMath from "remark-math";
import CodeBlock from "./code-block";

const imageFileExtensionsRegex =
  /\.(jpeg|jpg|gif|png|svg|webp|tiff|tif|bmp|ico|heif|heic|ico)$/i;
const newLineRegex = /\n/;
const newLineAtTheEndRegex = /\n$/;
const codeLanguageRegex = /language-(\w+)/;

// Utility function to check if all children are in Arabic or Hebrew
// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const isAllRtlText = (children: any): boolean => {
  let listText = children;
  // If children is not an array, wrap it in an array
  if (!Array.isArray(children)) {
    listText = [children];
  }
  // biome-ignore lint/suspicious/noExplicitAny: <explanation>
  return listText.every((child: any) => {
    if (child.props?.children) {
      return isAllRtlText(child.props.children);
    }
    if (typeof child === "string") {
      const trimmedChild = child.trim();
      if (trimmedChild === "") {
        return true; // Ignore check for newlines and empty strings
      }
      return isRtlText(trimmedChild);
    }
    return true; // Ignore check for nodes without props.children and non-string children
  });
};

export const MarkdownBlock = memo(({ text }: { text: string }) => {
  // Memoize the components object to prevent unnecessary re-renders
  const components = useMemo(
    () => ({
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      tr: ({ node: _, ...props }: any) => (
        <tr className="border-b" {...props} />
      ),
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      td: ({ node: _, children, ...props }: any) => {
        const isRtl = isRtlText(children);
        return (
          <td
            className={cn(
              "border-r border-gray-200 p-2 font-semibold",
              isRtl && "text-right",
            )}
            {...props}
          >
            {children}
          </td>
        );
      },
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      th: ({ node: _, children, ...props }: any) => {
        const isRtl = isRtlText(children);
        return (
          <th
            className={cn(
              "border-r border-gray-200 p-2 font-semibold",
              isRtl && "text-right",
            )}
            {...props}
          >
            {children}
          </th>
        );
      },
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      table: ({ node: _, ...props }: any) => (
        <table className="table-auto w-full mb-4" {...props} />
      ),
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      thead: ({ node: _, ...props }: any) => (
        <thead className="bg-gray-100 dark:bg-shallow text-left" {...props} />
      ),
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      tbody: ({ node: _, ...props }: any) => <tbody {...props} />,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      ol: ({ node: _, children, ...props }: any) => {
        const isRtl = isAllRtlText(children);
        return (
          <ol
            className={cn(
              "ml-5 mb-4 list-decimal",
              isRtl && "[direction:rtl] mr-4",
            )}
            {...props}
          >
            {children}
          </ol>
        );
      },
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      ul: ({ node: _, children, ...props }: any) => {
        const isRtl = isAllRtlText(children);
        return (
          <ul
            className={cn("ml-5 mb-4", isRtl && "[direction:rtl] mr-4")}
            {...props}
          >
            {children}
          </ul>
        );
      },
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      li: ({ node: _, ...props }: any) => <li className="ml-5" {...props} />,
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      p({ children, ...props }: any) {
        const isRtl = isRtlText(children);
        return (
          <p className={cn("text-left mb-4", isRtl && "text-right")} {...props}>
            {children}
          </p>
        );
      },
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      code({ className, children }: any) {
        const match = codeLanguageRegex.exec(className || "");
        const language = match ? match[1] : "";
        const content = String(children);

        if (language === "latex") {
          return (
            // hide children class="katex-html"
            <span className="[&_.katex-html]:hidden text-white">
              <Latex>{content}</Latex>
            </span>
          );
        }

        if (content.trim().startsWith("latex:")) {
          return <Latex>{content.slice(6)}</Latex>;
        }

        // Check if the content contains line breaks
        const isInline = !newLineRegex.test(content);
        return isInline ? (
          <code className={className}>{children}</code>
        ) : (
          <CodeBlock
            language={language}
            value={String(children).replace(newLineAtTheEndRegex, "")}
          />
        );
      },
      // biome-ignore lint/suspicious/noExplicitAny: <explanation>
      a({ href, children, ...props }: any) {
        // Check if the href is an image link
        const isImageLink = href && imageFileExtensionsRegex.test(href);
        if (isImageLink) {
          return (
            <div>
              <a href={href} {...props} target={"_blank"} rel="noreferrer">
                {children}
              </a>
              <div className="mt-2">
                <Image
                  src={href}
                  alt={`Image at ${href}`}
                  className="max-w-full h-auto"
                />
              </div>
            </div>
          );
        }
        // Default link rendering for non-image links
        return (
          <a
            href={href}
            {...props}
            target={"_blank"}
            rel="noreferrer"
            className="text-primaryaccent hover:text-[#0AAFC8]"
          >
            {children}
          </a>
        );
      },
    }),
    [],
  ); // Empty dependency array since these components don't depend on any props

  // Memoize the ReactMarkdown component to prevent unnecessary re-renders
  const markdownContent = useMemo(
    () => (
      <ReactMarkdown
        className={"font-inter pl-10px text-[0.9rem]"}
        components={components}
        remarkPlugins={[remarkGfm, remarkMath]}
      >
        {text}
      </ReactMarkdown>
    ),
    [text, components],
  );

  return markdownContent;
});
